import React, { useState, useEffect } from 'react';
import { TrendingUp, Users, Package, MapPin, TruckIcon as Truck, Archive } from 'lucide-react';
import { getDocuments } from '../../services/firebase';
import { seedDailySalesDetails } from '../../services/salesTransactions';
import { User, Product, Sale } from '../../types';

interface Props {
  currentUser?: User;
  onNavigateToDistributionNetwork?: () => void;
}

const OverviewSection: React.FC<Props> = ({ currentUser, onNavigateToDistributionNetwork }) => {
  const [salesmen, setSalesmen] = useState<User[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [sales, setSales] = useState<Sale[]>([]);
  const [isSeeding, setIsSeeding] = useState(false);
  const [seedingMessage, setSeedingMessage] = useState('');
  const [stats, setStats] = useState({
    totalSalesmen: 0,
    totalProducts: 0,
    todaySales: 0,
    totalOutlets: 0,
    pendingOrders: 0,
    lowStockItems: 0
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        const [salesmenData, productsData, salesData] = await Promise.all([
          getDocuments('users'),
          getDocuments('products'),
          getDocuments('sales')
        ]);

        const salesmenUsers = salesmenData.filter((user: any) => user.role === 'salesman');
        setSalesmen(salesmenUsers);
        setProducts(productsData as Product[]);
        setSales(salesData as Sale[]);

        const todaySales = salesData.reduce((sum: number, sale: any) => sum + sale.grandTotal, 0);
        setStats({
          totalSalesmen: salesmenUsers.length,
          totalProducts: productsData.length,
          todaySales,
          totalOutlets: 0,
          pendingOrders: 0,
          lowStockItems: 0
        });
      } catch (err) {
        console.error('Error loading overview data:', err);
      }
    };

    loadData();
  }, []);

  const handlePrepareDailySales = async () => {
    setIsSeeding(true);
    setSeedingMessage('Preparing daily sales data...');
    try {
      const today = new Date().toISOString().split('T')[0];
      const warehouseId = currentUser?.warehouseId || 'default-warehouse';
      const result = await seedDailySalesDetails(
        today,
        warehouseId,
        currentUser?.id || 'system'
      );

      if (result.success) {
        setSeedingMessage('✅ Daily sales prepared successfully!');
      } else {
        setSeedingMessage('❌ Failed to prepare daily sales.');
      }
    } catch (error: any) {
      setSeedingMessage(`❌ Error: ${error}`);
    } finally {
      setIsSeeding(false);
      setTimeout(() => setSeedingMessage(''), 5000);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Warehouse Overview</h2>
          <p className="text-gray-600">Welcome back, {currentUser?.name}</p>
        </div>
        <div className="flex items-center space-x-4">
          <button
            onClick={handlePrepareDailySales}
            disabled={isSeeding}
            className="flex items-center space-x-3 px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-base font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <TrendingUp className="w-5 h-5" />
            <span>{isSeeding ? 'Preparing...' : 'Prepare Daily Sales'}</span>
          </button>
          {seedingMessage && (
            <div className="px-4 py-2 text-sm bg-gray-100 rounded-lg">
              {seedingMessage}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-4 gap-8">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Total Salesmen</p>
              <p className="text-3xl font-bold text-blue-600">{stats.totalSalesmen}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Total Products</p>
              <p className="text-3xl font-bold text-green-600">{stats.totalProducts}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Package className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Today's Sales</p>
              <p className="text-3xl font-bold text-purple-600">₹{stats.todaySales.toLocaleString()}</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>

        <button
          onClick={onNavigateToDistributionNetwork}
          className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:bg-orange-50 hover:border-orange-200 transition-all duration-200 cursor-pointer w-full text-left"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Total Outlets</p>
              <p className="text-3xl font-bold text-orange-600">{stats.totalOutlets}</p>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <MapPin className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </button>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Pending Orders</p>
              <p className="text-3xl font-bold text-red-600">{stats.pendingOrders}</p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <Truck className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Low Stock Items</p>
              <p className="text-3xl font-bold text-yellow-600">{stats.lowStockItems}</p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Archive className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Recent Salesmen Performance</h3>
        <div className="space-y-3">
          {salesmen.slice(0, 5).map((salesman) => (
            <div key={salesman.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-medium text-sm">
                    {salesman.name.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="font-medium text-gray-800">{salesman.name}</p>
                  <p className="text-sm text-gray-600">{salesman.email}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm font-medium text-green-600">Active</p>
                <p className="text-xs text-gray-500">Last seen: Today</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default OverviewSection;

